declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
        retry?: 'auto' | 'never';
        size?: 'normal' | 'compact' | 'invisible';
        appearance?: 'always' | 'execute' | 'interaction-only';
        theme?: 'light' | 'dark' | 'auto';
      }) => string;
      reset?: (widgetId?: string) => void;
      execute?: (widgetId?: string) => void;
    };
  }
}

let currentToken: string | null = null;
let refresher: (() => void) | null = null;
let tokenWaiters: Array<(token: string) => void> = [];

export function setTurnstileToken(token: string | null) {
  currentToken = token;
  if (token && tokenWaiters.length > 0) {
    const waiters = tokenWaiters;
    tokenWaiters = [];
    waiters.forEach((resolve) => resolve(token));
  }
}

export function getTurnstileToken(): string | null {
  return currentToken;
}

export function clearTurnstileToken() {
  currentToken = null;
}

/**
 * Lets the mounted Turnstile widget expose a way to re-run its challenge. The widget owns the
 * widgetId, so API-level code can't reset it directly; registering a refresher here is what makes
 * refreshTurnstileToken possible. Pass null on unmount.
 */
export function registerTurnstileRefresher(refresh: (() => void) | null) {
  refresher = refresh;
}

/**
 * Forces the widget to re-run its challenge and resolves with the fresh token, or null if no widget
 * is mounted or no token arrives within the timeout. Used to recover when the backend rejects a
 * stale/consumed token mid-flow (e.g. a long multi-file download outliving the token's validity).
 */
export function refreshTurnstileToken(timeoutMs = 15000): Promise<string | null> {
  if (!refresher) {
    return Promise.resolve(null);
  }

  clearTurnstileToken();
  try {
    refresher();
  } catch {
    return Promise.resolve(null);
  }

  return new Promise<string | null>((resolve) => {
    const onToken = (token: string) => {
      clearTimeout(timer);
      resolve(token);
    };
    const timer = setTimeout(() => {
      tokenWaiters = tokenWaiters.filter((waiter) => waiter !== onToken);
      resolve(null);
    }, timeoutMs);
    tokenWaiters.push(onToken);
  });
}
